import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInspectorSections } from './use-inspector-sections';
import { CollapsibleContent } from './collapsible-content';
import {
  FEATURE_ALIGNMENT_CONTROL,
  FEATURE_CAPTIONS_PAGE_DURATION_CONTROL,
  FEATURE_COLOR_CONTROL,
  FEATURE_DIMENSIONS_CONTROL,
  FEATURE_FONT_FAMILY_CONTROL,
  FEATURE_FONT_STYLE_CONTROL,
  FEATURE_OPACITY_CONTROL,
  FEATURE_POSITION_CONTROL,
  FEATURE_ROTATION_CONTROL,
  FEATURE_TEXT_ALIGNMENT_CONTROL,
  FEATURE_TEXT_DIRECTION_CONTROL,
  FEATURE_TEXT_FONT_SIZE_CONTROL,
  FEATURE_TEXT_LETTER_SPACING_CONTROL,
  FEATURE_TEXT_LINE_HEIGHT_CONTROL,
  FEATURE_TEXT_MAX_LINES_CONTROL,
  FEATURE_TEXT_STROKE_COLOR_CONTROL,
  FEATURE_TEXT_STROKE_WIDTH_CONTROL,
  FEATURE_TOKENS_CONTROL,
} from '../flags';
import { CaptionsItem } from '../items/captions/captions-item-type';
import {
  defaultTextShadow,
  defaultTextItemBackground,
} from '../items/text/text-item-type';
import { ColorInspector } from './color-inspector';
import {
  AccordionHeader,
  cn,
  ControlsHeader,
  ToggleGroup,
  ToggleItem,
  ToolButton,
} from '@crabs-ai/ds-react';
import {
  Delete,
  Remove,
  Visibility,
  VisibilityOffFill,
} from '@crabs-ai/web-icons';
import { AlignmentControls } from './controls/alignment-controls';
import { PageDurationControls } from './controls/caption-controls/page-duration-controls';
import { TokensControls } from './controls/caption-controls/tokens-controls';
import { AdditionalDisplayTimeControls } from './controls/caption-controls/additional-display-time-controls';
import { CaptionTranslationControls } from './controls/caption-controls/caption-translation-controls';
import { DimensionsControls } from './controls/dimensions-controls';
import { FontFamilyControl } from './controls/font-family-controls/font-family-controls';
import { FontSizeControls } from './controls/font-size-controls';
import { FontStyleControls } from './controls/font-style-controls/font-style-controls';
import { LetterSpacingControls } from './controls/letter-spacing-controls';
import { LineHeightControls } from './controls/line-height-controls';
import { MaxLinesControls } from './controls/max-lines-controls';
import { OpacityControls } from './controls/opacity-controls';
import { PositionControl } from './controls/position-control';
import { RotationControl } from './controls/rotation-controls';
import { StrokeWidthControls } from './controls/stroke-width-controls';
import { TextAlignmentControls } from './controls/text-alignment-controls';
import { TextDirectionControls } from './controls/text-direction-controls';
import ShadowControls from '@/features/remotion-editor/editor/inspector/controls/shadow-controls';
import BackgroundControls from '@/features/remotion-editor/editor/inspector/controls/background-controls';
import { AnimationControls } from './controls/animation-controls';
import { EMPTY_ANIMATIONS } from '../items/animation/animation-item-type';
import { useTranslations } from 'next-intl';
import { useFeatureEnabled } from '@/shared/hooks/useFeatureEnabled';
import { changeItemsWithSelection } from '../state/actions/change-items';
import { useWriteContext } from '../utils/use-context';
import { useTargetItemIds } from './utils/use-target-item-ids';
import { InspectorSubLabel } from './components/inspector-label';
import {
  CAPTION_HIGHLIGHT_EFFECT_OPTIONS,
  type CaptionHighlightEffect,
} from '@/shared/lib/captions/caption-highlight-effect';
import { DtoProjectType } from '@/shared/constants';
import {
  useIsLtsProject,
  useProjectTypeContext,
} from '@/features/remotion-editor/editor/contexts/project-type-context';
import { useTenantUiConfig } from '@/shared/hooks/useTenantUiConfig';

const CAPTIONS_DEFAULTS = {
  displayTime: true,
  captionTranslation: true,
  positionShape: true,
  font: true,
  color: true,
  stroke: true,
  background: true,
  shadow: true,
  captionDetail: true,
};

const normalizeHighlightColor = (
  color: string | null | undefined,
): string | null => {
  // TODO: 현재 Caption Highlight는 null을 "미적용(None)"의 단일 의미로 사용한다.
  // 추후 기획에서 null/transparent를 UI에서 명확히 구분해야 하면
  // 데이터 모델과 토글 동작(eye off/on)도 함께 분리해 재정의한다.
  if (color == null || color === 'transparent') return null;
  return color;
};

const CaptionsInspectorUnmemoized: React.FC<{
  item: CaptionsItem;
  isMulti?: boolean;
  showCaptionDetailOption?: boolean;
  collapseAllTrigger?: number;
  expandAllTrigger?: number;
}> = ({
  item,
  isMulti,
  showCaptionDetailOption = true,
  collapseAllTrigger = 0,
  expandAllTrigger = 0,
}) => {
  const t = useTranslations();
  const { enable: isOperator } = useFeatureEnabled();
  const { projectType } = useProjectTypeContext();
  // LTS 자막 번역 — space 정책(showLtsCaptionTranslation)이 켜진 LTS 프로젝트에서만 노출한다.
  const isLtsProject = useIsLtsProject();
  const { showLtsCaptionTranslation } = useTenantUiConfig();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(item.id);

  // ── Active 상태 (로컬 state) ──
  const deriveSectionActive = useCallback(
    (_id: string) => {
      const derived = {
        color: item.color !== 'transparent',
        stroke: item.strokeWidth > 0,
        background: item.background?.visible === true,
        shadow: item.shadow?.visible === true,
      };
      return derived;
    },
    [
      item.color,
      item.strokeWidth,
      item.background?.visible,
      item.shadow?.visible,
    ],
  );

  const [sectionActive, setSectionActive] = useState(() =>
    deriveSectionActive(item.id),
  );

  useEffect(() => {
    setSectionActive(deriveSectionActive(item.id));
  }, [item.id, deriveSectionActive]);

  const { openSections, setOpenSections, toggleSection } = useInspectorSections(
    {
      itemId: item.id,
      defaults: CAPTIONS_DEFAULTS,
      collapseAllTrigger,
      expandAllTrigger,
      sectionActive,
    },
  );
  const isColorEyeOff = item.colorVisible === false;
  const isStrokeEyeOff = item.strokeVisible === false;
  const isHighlightEyeOff = item.highlightVisible === false;
  const isBackgroundEyeOff = item.backgroundVisible === false;
  const isShadowEyeOff = item.shadowVisible === false;

  // ── Color 핸들러 ──
  const prevHighlightColorRef = useRef<string | null>(
    normalizeHighlightColor(item.highlightColor),
  );

  const handleActivateColor = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => ({
          ...i,
          color: '#FFFFFF',
          highlightColor: prevHighlightColorRef.current ?? null,
          colorVisible: true,
        })),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, color: true }));
    setOpenSections((prev) => ({ ...prev, color: true }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleDeactivateColor = useCallback(() => {
    const normalizedHighlightColor = normalizeHighlightColor(
      item.highlightColor,
    );
    if (normalizedHighlightColor != null) {
      prevHighlightColorRef.current = normalizedHighlightColor;
    }
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => ({
          ...i,
          color: 'transparent',
          highlightColor: null,
          colorVisible: true,
          highlightVisible: true,
        })),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, color: false }));
    setOpenSections((prev) => ({ ...prev, color: false }));
  }, [
    setState,
    item.id,
    targetIds,
    item.color,
    item.highlightColor,
    setOpenSections,
  ]);

  const handleToggleColorVisibility = useCallback(() => {
    const nextVisible = isColorEyeOff;
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => ({
          ...i,
          colorVisible: nextVisible,
        })),
      commitToUndoStack: true,
    });
  }, [isColorEyeOff, setState, item.id, targetIds]);

  const handleToggleHighlightVisibility = useCallback(() => {
    const nextVisible = isHighlightEyeOff;
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => ({
          ...i,
          highlightVisible: nextVisible,
        })),
      commitToUndoStack: true,
    });
  }, [isHighlightEyeOff, setState, item.id, targetIds, item.highlightColor]);

  const handleChangeHighlightEffect = useCallback(
    (highlightEffect: CaptionHighlightEffect) => {
      setState({
        update: (state) =>
          changeItemsWithSelection(state, item.id, targetIds, (i) => {
            if (i.type !== 'captions') return i;
            return { ...i, highlightEffect };
          }),
        commitToUndoStack: true,
      });
    },
    [setState, item.id, targetIds],
  );

  // '-' 버튼: 하이라이트 효과 미적용(None) = null
  const handleRemoveHighlightColor = useCallback(() => {
    const normalizedHighlightColor = normalizeHighlightColor(
      item.highlightColor,
    );
    if (normalizedHighlightColor != null) {
      prevHighlightColorRef.current = normalizedHighlightColor;
    }
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => ({
          ...i,
          highlightColor: null,
          highlightVisible: true,
        })),
      commitToUndoStack: true,
    });
  }, [setState, item.id, targetIds, item.highlightColor]);

  // ── Stroke 핸들러 ──
  const handleActivateStroke = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'text' && i.type !== 'captions') return i;
          return {
            ...i,
            strokeWidth: 10,
            strokeColor: '#000000',
            strokeVisible: true,
          };
        }),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, stroke: true }));
    setOpenSections((prev) => ({ ...prev, stroke: true }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleDeactivateStroke = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'text' && i.type !== 'captions') return i;
          return {
            ...i,
            strokeWidth: 0,
            strokeColor: 'transparent',
            strokeVisible: true,
          };
        }),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, stroke: false }));
    setOpenSections((prev) => ({ ...prev, stroke: false }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleToggleStrokeVisibility = useCallback(() => {
    const nextVisible = isStrokeEyeOff;
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'text' && i.type !== 'captions') return i;
          return { ...i, strokeVisible: nextVisible };
        }),
      commitToUndoStack: true,
    });
  }, [isStrokeEyeOff, setState, item.id, targetIds]);

  // ── Background 핸들러 ──
  const handleActivateBackground = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'text' && i.type !== 'captions') return i;
          return {
            ...i,
            background: { ...defaultTextItemBackground, visible: true },
            backgroundVisible: true,
          };
        }),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, background: true }));
    setOpenSections((prev) => ({ ...prev, background: true }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleDeactivateBackground = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'text' && i.type !== 'captions') return i;
          return {
            ...i,
            background: { ...defaultTextItemBackground },
            backgroundVisible: undefined,
          };
        }),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, background: false }));
    setOpenSections((prev) => ({ ...prev, background: false }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleToggleBackgroundVisibility = useCallback(() => {
    const nextVisible = isBackgroundEyeOff;
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'text' && i.type !== 'captions') return i;
          return { ...i, backgroundVisible: nextVisible };
        }),
      commitToUndoStack: true,
    });
  }, [isBackgroundEyeOff, setState, item.id, targetIds]);

  // ── Shadow 핸들러 ──
  const handleActivateShadow = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'text' && i.type !== 'captions') return i;
          return {
            ...i,
            shadow: { ...defaultTextShadow, visible: true },
            shadowVisible: true,
          };
        }),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, shadow: true }));
    setOpenSections((prev) => ({ ...prev, shadow: true }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleDeactivateShadow = useCallback(() => {
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'text' && i.type !== 'captions') return i;
          return {
            ...i,
            shadow: { ...defaultTextShadow },
            shadowVisible: undefined,
          };
        }),
      commitToUndoStack: true,
    });
    setSectionActive((prev) => ({ ...prev, shadow: false }));
    setOpenSections((prev) => ({ ...prev, shadow: false }));
  }, [setState, item.id, targetIds, setOpenSections]);

  const handleToggleShadowVisibility = useCallback(() => {
    const nextVisible = isShadowEyeOff;
    setState({
      update: (state) =>
        changeItemsWithSelection(state, item.id, targetIds, (i) => {
          if (i.type !== 'text' && i.type !== 'captions') return i;
          return { ...i, shadowVisible: nextVisible };
        }),
      commitToUndoStack: true,
    });
  }, [isShadowEyeOff, setState, item.id, targetIds]);

  return (
    <div className={'flex flex-col'}>
      <div className="flex flex-col gap-2xs">
        {/* Animation 섹션 */}
        <div className="border-b border-sys-style-neutral-light">
          <AnimationControls
            itemId={item.id}
            animations={item.animations ?? EMPTY_ANIMATIONS}
            durationInFrames={item.durationInFrames}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </div>
        {projectType === DtoProjectType.agentVideoCaption && (
          <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
            <AccordionHeader
              size="sm"
              title={t('Editor.inspector.caption-display-time.title')}
              isOpened={openSections.displayTime}
              onClick={() => toggleSection('displayTime')}
            />
            <CollapsibleContent isOpen={openSections.displayTime}>
              <div className="px-md py-4xs">
                <AdditionalDisplayTimeControls itemId={item.id} />
              </div>
            </CollapsibleContent>
          </div>
        )}
        {isLtsProject && showLtsCaptionTranslation && (
          <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
            <AccordionHeader
              size="sm"
              title={t('Editor.inspector.caption-translation.title')}
              isOpened={openSections.captionTranslation}
              onClick={() => toggleSection('captionTranslation')}
            />
            <CollapsibleContent isOpen={openSections.captionTranslation}>
              <div className="px-md py-4xs">
                <CaptionTranslationControls />
              </div>
            </CollapsibleContent>
          </div>
        )}
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={t('Editor.inspector.text.position-shape')}
            isOpened={openSections.positionShape}
            onClick={() => toggleSection('positionShape')}
          />
          <CollapsibleContent isOpen={openSections.positionShape}>
            <div className="flex flex-col gap-4xs px-md py-4xs">
              {FEATURE_ALIGNMENT_CONTROL && (
                <AlignmentControls itemId={item.id} />
              )}
              {FEATURE_POSITION_CONTROL && <PositionControl itemId={item.id} />}
              {FEATURE_DIMENSIONS_CONTROL && (
                <DimensionsControls itemId={item.id} />
              )}
              {FEATURE_ROTATION_CONTROL && (
                <RotationControl
                  rotation={item.rotation}
                  itemId={item.id}
                />
              )}
              {FEATURE_OPACITY_CONTROL && (
                <div className="w-full">
                  <OpacityControls
                    opacity={item.opacity}
                    itemId={item.id}
                  />
                </div>
              )}
            </div>
          </CollapsibleContent>
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={t('Editor.inspector.text.font')}
            isOpened={openSections.font}
            onClick={() => toggleSection('font')}
          />
          <CollapsibleContent isOpen={openSections.font}>
            <div className="flex flex-col gap-4xs px-md py-4xs">
              {FEATURE_FONT_FAMILY_CONTROL && (
                <FontFamilyControl
                  fontFamily={item.fontFamily}
                  itemId={item.id}
                />
              )}
              <div className="flex items-center gap-4xs">
                {FEATURE_FONT_STYLE_CONTROL && (
                  <FontStyleControls
                    fontFamily={item.fontFamily}
                    fontStyle={item.fontStyle}
                    itemId={item.id}
                  />
                )}
                {FEATURE_TEXT_FONT_SIZE_CONTROL && (
                  <FontSizeControls
                    fontSize={item.fontSize}
                    itemId={item.id}
                    itemType="captions"
                  />
                )}
              </div>
              <div className="flex flex-row gap-2">
                {FEATURE_TEXT_LINE_HEIGHT_CONTROL && (
                  <LineHeightControls
                    lineHeight={item.lineHeight}
                    itemId={item.id}
                  />
                )}
                {FEATURE_TEXT_LETTER_SPACING_CONTROL && (
                  <LetterSpacingControls
                    letterSpacing={item.letterSpacing}
                    itemId={item.id}
                  />
                )}
              </div>
              <div className="flex flex-row gap-2">
                {FEATURE_TEXT_ALIGNMENT_CONTROL && (
                  <TextAlignmentControls
                    align={item.align}
                    itemId={item.id}
                  />
                )}
                {FEATURE_TEXT_DIRECTION_CONTROL && (
                  <TextDirectionControls
                    direction={item.direction}
                    itemId={item.id}
                  />
                )}
              </div>
            </div>
          </CollapsibleContent>
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <ControlsHeader
            size="sm"
            title={t('Editor.inspector.text.color')}
            isActive={sectionActive.color}
            isOpened={openSections.color}
            onAction={handleActivateColor}
            onClick={() => toggleSection('color')}
            actionTooltip="속성 추가"
            activeTrailingNode={
              <ToolButton
                size="sm"
                onClick={handleDeactivateColor}
                isShowTooltip
                tooltipNode="속성 삭제"
              >
                <Delete />
              </ToolButton>
            }
          />
          <CollapsibleContent
            isOpen={sectionActive.color && openSections.color}
          >
            <div className={'flex flex-col gap-4xs px-md py-4xs'}>
              <div className="flex items-end-safe gap-4xs">
                <div
                  className={cn(
                    'flex flex-1 items-end-safe gap-4xs',
                    isColorEyeOff && 'pointer-events-none opacity-40',
                  )}
                >
                  {FEATURE_COLOR_CONTROL && (
                    <ColorInspector
                      color={item.color}
                      itemId={item.id}
                      colorType="color"
                      accessibilityLabel="Fill color"
                      itemOpacity={item.opacity}
                    />
                  )}
                </div>
                <ToolButton
                  aria-label="Toggle color visibility"
                  onClick={handleToggleColorVisibility}
                  isShowTooltip
                  tooltipNode={!isColorEyeOff ? '속성 끄기' : '속성 켜기'}
                  state={isColorEyeOff ? 'selected' : 'default'}
                  isTransparent={false}
                  className="h-[36px] w-[36px]"
                >
                  {!isColorEyeOff ? (
                    <Visibility className="size-4" />
                  ) : (
                    <VisibilityOffFill className="size-4" />
                  )}
                </ToolButton>
                <ToolButton
                  aria-label="Remove color"
                  onClick={handleDeactivateColor}
                  isShowTooltip
                  tooltipNode="속성 삭제"
                  className="h-[36px] w-[36px] text-sys-cont-neutral-default"
                >
                  <Remove className="size-4" />
                </ToolButton>
              </div>
              <div className="flex items-end-safe gap-4xs">
                <div
                  className={cn(
                    'flex flex-1 items-end-safe gap-4xs',
                    isHighlightEyeOff && 'pointer-events-none opacity-40',
                  )}
                >
                  {FEATURE_COLOR_CONTROL && (
                    <ColorInspector
                      color={normalizeHighlightColor(item.highlightColor)}
                      itemId={item.id}
                      colorType="highlightColor"
                      accessibilityLabel="Highlight color"
                      itemOpacity={item.opacity}
                    />
                  )}
                </div>
                <ToolButton
                  aria-label="Toggle highlight visibility"
                  onClick={handleToggleHighlightVisibility}
                  isShowTooltip
                  tooltipNode={!isHighlightEyeOff ? '속성 끄기' : '속성 켜기'}
                  state={isHighlightEyeOff ? 'selected' : 'default'}
                  isTransparent={false}
                  className="h-[36px] w-[36px]"
                >
                  {!isHighlightEyeOff ? (
                    <Visibility className="size-4" />
                  ) : (
                    <VisibilityOffFill className="size-4" />
                  )}
                </ToolButton>
                <ToolButton
                  aria-label="Remove highlight color"
                  onClick={handleRemoveHighlightColor}
                  isShowTooltip
                  tooltipNode="속성 삭제"
                  className="h-[36px] w-[36px] text-sys-cont-neutral-default"
                >
                  <Remove className="size-4" />
                </ToolButton>
              </div>
              <div className="flex flex-col gap-2xs">
                <InspectorSubLabel>자막 강조 효과</InspectorSubLabel>
                <ToggleGroup
                  type="single"
                  value={item.highlightEffect ?? 'instant'}
                  onValueChange={(highlightEffect) => {
                    if (
                      highlightEffect &&
                      typeof highlightEffect === 'string'
                    ) {
                      handleChangeHighlightEffect(
                        highlightEffect as CaptionHighlightEffect,
                      );
                    }
                  }}
                  className="flex flex-wrap"
                >
                  {CAPTION_HIGHLIGHT_EFFECT_OPTIONS.map((option) => (
                    <ToggleItem
                      key={option.value}
                      value={option.value}
                      aria-label={option.label}
                      label={option.label}
                      showIcon={false}
                    />
                  ))}
                </ToggleGroup>
              </div>
            </div>
          </CollapsibleContent>
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <ControlsHeader
            size="sm"
            title={t('Editor.inspector.text.stroke')}
            isActive={sectionActive.stroke}
            isOpened={openSections.stroke}
            onAction={handleActivateStroke}
            onClick={() => toggleSection('stroke')}
            actionTooltip="속성 추가"
            activeTrailingNode={
              <ToolButton
                size="sm"
                onClick={handleDeactivateStroke}
                isShowTooltip
                tooltipNode="속성 삭제"
              >
                <Delete />
              </ToolButton>
            }
          />
          <CollapsibleContent
            isOpen={sectionActive.stroke && openSections.stroke}
          >
            <div className="flex flex-col gap-4xs px-md py-4xs">
              <div className="flex items-end-safe gap-4xs">
                <div
                  className={cn(
                    'flex flex-1 items-end-safe gap-4xs',
                    isStrokeEyeOff && 'pointer-events-none opacity-40',
                  )}
                >
                  {FEATURE_TEXT_STROKE_COLOR_CONTROL && (
                    <ColorInspector
                      color={item.strokeColor}
                      itemId={item.id}
                      colorType="strokeColor"
                      accessibilityLabel="Stroke color"
                      itemOpacity={item.opacity}
                    />
                  )}
                </div>
                <ToolButton
                  aria-label="Toggle stroke visibility"
                  onClick={handleToggleStrokeVisibility}
                  isShowTooltip
                  tooltipNode={!isStrokeEyeOff ? '속성 끄기' : '속성 켜기'}
                  state={isStrokeEyeOff ? 'selected' : 'default'}
                  isTransparent={false}
                  className="h-[36px] w-[36px]"
                >
                  {!isStrokeEyeOff ? (
                    <Visibility className="size-4" />
                  ) : (
                    <VisibilityOffFill className="size-4" />
                  )}
                </ToolButton>
                <ToolButton
                  aria-label="Remove stroke"
                  onClick={handleDeactivateStroke}
                  isShowTooltip
                  tooltipNode="속성 삭제"
                  className="h-[36px] w-[36px] text-sys-cont-neutral-default"
                >
                  <Remove className="size-4" />
                </ToolButton>
              </div>
              <div
                className={cn(
                  isStrokeEyeOff && 'pointer-events-none opacity-40',
                )}
              >
                {FEATURE_TEXT_STROKE_WIDTH_CONTROL && (
                  <StrokeWidthControls
                    strokeWidth={item.strokeWidth}
                    itemId={item.id}
                  />
                )}
              </div>
            </div>
          </CollapsibleContent>
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <ControlsHeader
            size="sm"
            title={t('Editor.inspector.text-background.title')}
            isActive={sectionActive.background}
            isOpened={openSections.background}
            onAction={handleActivateBackground}
            onClick={() => toggleSection('background')}
            actionTooltip="속성 추가"
            activeTrailingNode={
              <ToolButton
                size="sm"
                onClick={handleDeactivateBackground}
                isShowTooltip
                tooltipNode="속성 삭제"
              >
                <Delete />
              </ToolButton>
            }
          />
          <CollapsibleContent
            isOpen={sectionActive.background && openSections.background}
          >
            <div className={'px-md py-4xs'}>
              <BackgroundControls
                itemId={item.id}
                background={item.background}
                isEyeOff={isBackgroundEyeOff}
                onToggleEyeVisibility={handleToggleBackgroundVisibility}
                onDeactivate={() => {
                  setSectionActive((prev) => ({ ...prev, background: false }));
                  setOpenSections((prev) => ({ ...prev, background: false }));
                }}
              />
            </div>
          </CollapsibleContent>
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <ControlsHeader
            size="sm"
            title={t('Editor.inspector.shadow-style')}
            isActive={sectionActive.shadow}
            isOpened={openSections.shadow}
            onAction={handleActivateShadow}
            onClick={() => toggleSection('shadow')}
            actionTooltip="속성 추가"
            activeTrailingNode={
              <ToolButton
                size="sm"
                onClick={handleDeactivateShadow}
                isShowTooltip
                tooltipNode="속성 삭제"
              >
                <Delete />
              </ToolButton>
            }
          />
          <CollapsibleContent
            isOpen={sectionActive.shadow && openSections.shadow}
          >
            <div className={'px-md py-4xs'}>
              <ShadowControls
                itemId={item.id}
                shadow={item.shadow}
                isEyeOff={isShadowEyeOff}
                onToggleEyeVisibility={handleToggleShadowVisibility}
                onDeactivate={() => {
                  setSectionActive((prev) => ({ ...prev, shadow: false }));
                  setOpenSections((prev) => ({ ...prev, shadow: false }));
                }}
              />
            </div>
          </CollapsibleContent>
        </div>
        {/* TODO: 캡션 세부 옵션 & 카라오케 섹션 - 추후 새로운 UI/UX로 재구현 예정
        {showCaptionDetailOption && isOperator && !isMulti && (
          <>
            <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
              <AccordionHeader
                size="sm"
                title={t('Editor.inspector.caption-detail-option')}
                isOpened={openSections.captionDetail}
                onClick={() => toggleSection('captionDetail')}
              />
              {openSections.captionDetail && (
                <div className="flex flex-col gap-4xs px-md py-4xs">
                  {FEATURE_CAPTIONS_PAGE_DURATION_CONTROL && (
                    <PageDurationControls
                      pageDurationInMilliseconds={
                        item.pageDurationInMilliseconds
                      }
                      itemId={item.id}
                    />
                  )}
                  {FEATURE_TEXT_MAX_LINES_CONTROL && (
                    <MaxLinesControls
                      maxLines={item.maxLines}
                      itemId={item.id}
                    />
                  )}
                </div>
              )}
            </div>
            {FEATURE_TOKENS_CONTROL && <TokensControls item={item} />}
          </>
        )}
        */}
      </div>
      <div className={'layout-padding-bottom'} />
    </div>
  );
};

export const CaptionsInspector = React.memo(CaptionsInspectorUnmemoized);
